import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { 
  BarChart3, 
  Building2, 
  FileText, 
  Plus, 
  Search, 
  Settings, 
  Lightbulb,
  Target,
  Users,
  MessageSquare,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface Company {
  id: string;
  name: string;
}

interface CommandBarProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAICommand?: (command: string) => void;
}

export function CommandBar({ open, onOpenChange, onAICommand }: CommandBarProps) {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (open) {
      loadCompanies();
    }
  }, [open]);

  const loadCompanies = async () => {
    const { data } = await supabase
      .from("companies")
      .select("id, name")
      .limit(10);
    setCompanies(data || []);
  };

  const runCommand = useCallback((command: () => void) => {
    onOpenChange(false);
    command();
  }, [onOpenChange]);

  const aiCommands = [
    { label: "What's the current status?", icon: BarChart3 },
    { label: "What needs attention?", icon: Target },
    { label: "Generate this week's report", icon: FileText },
    { label: "What should I focus on?", icon: Lightbulb },
  ];

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput 
        placeholder="Type a command or search..." 
        value={searchQuery}
        onValueChange={setSearchQuery}
      />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        {/* AI Commands */}
        <CommandGroup heading="Ask Memo AI">
          {aiCommands.map((cmd) => (
            <CommandItem
              key={cmd.label}
              onSelect={() => runCommand(() => onAICommand?.(cmd.label))}
            >
              <cmd.icon className="mr-2 h-4 w-4 text-primary" />
              <span>{cmd.label}</span>
            </CommandItem>
          ))}
          {searchQuery && !aiCommands.some(c => c.label.toLowerCase().includes(searchQuery.toLowerCase())) && (
            <CommandItem
              onSelect={() => runCommand(() => onAICommand?.(searchQuery))}
            >
              <MessageSquare className="mr-2 h-4 w-4 text-primary" />
              <span>Ask: "{searchQuery}"</span>
            </CommandItem>
          )}
        </CommandGroup>

        <CommandSeparator />

        {/* Quick Actions */}
        <CommandGroup heading="Quick Actions">
          <CommandItem onSelect={() => runCommand(() => navigate("/app/companies/new"))}>
            <Plus className="mr-2 h-4 w-4" />
            <span>Create new company</span>
          </CommandItem>
          <CommandItem onSelect={() => runCommand(() => navigate("/app/portfolio"))}>
            <BarChart3 className="mr-2 h-4 w-4" />
            <span>View portfolio dashboard</span>
          </CommandItem>
          <CommandItem onSelect={() => runCommand(() => navigate("/app/profile"))}>
            <Settings className="mr-2 h-4 w-4" />
            <span>Profile settings</span>
          </CommandItem>
        </CommandGroup>

        <CommandSeparator />

        {/* Companies */}
        {companies.length > 0 && (
          <CommandGroup heading="Your Companies">
            {companies.map((company) => (
              <CommandItem
                key={company.id}
                onSelect={() => runCommand(() => navigate(`/app/companies/${company.id}`))}
              >
                <Building2 className="mr-2 h-4 w-4" />
                <span>{company.name}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}

// Hook to manage command bar state
export function useCommandBar() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  return { open, setOpen };
}
